import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const waTemplate = (name, bundle) =>
    encodeURIComponent(
        `Assalamu Alaikum${name ? " " + name : ""}, this is Resham Drops. We noticed you were about to order${bundle ? ` the ${bundle}` : " our serum"} but did not complete checkout. Would you like us to confirm your Cash-on-Delivery order right here? Reply YES and we'll process it for you.`,
    );

export default function AdminAbandoned() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/admin/abandoned-carts")
            .then((r) => setRows(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const remove = async (id) => {
        try {
            await api.delete(`/admin/abandoned-carts/${id}`);
            toast.success("Dismissed");
            setRows((r) => r.filter((x) => x.id !== id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const ONE_HOUR_MS = 60 * 60 * 1000;

    return (
        <div data-testid="admin-abandoned" className="space-y-6">
            <header>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                    Abandoned checkouts
                </h1>
                <p className="text-sm text-forest-900/70 mt-1">
                    Visitors who opened the checkout popup, filled details, and left without ordering. Nudge them on WhatsApp after about one hour for the warmest conversion.
                </p>
            </header>

            <div className="bg-white rounded-2xl border border-forest-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-forest-50 text-forest-800 text-left text-[11px] uppercase tracking-widest">
                            <tr>
                                <th className="px-4 py-3">Visitor</th>
                                <th className="px-4 py-3">Phone</th>
                                <th className="px-4 py-3">City</th>
                                <th className="px-4 py-3">Bundle</th>
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-forest-700/60">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-forest-700/60">
                                        No abandoned checkouts right now.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => {
                                const created = new Date(r.created_at);
                                const ageMs = Date.now() - created.getTime();
                                const ready = ageMs >= ONE_HOUR_MS;
                                const waNumber = (r.phone || "").replace(/^0/, "");
                                const link = r.phone
                                    ? `https://wa.me/92${waNumber}?text=${waTemplate(r.customer_name, r.bundle_label)}`
                                    : null;
                                return (
                                    <tr
                                        key={r.id}
                                        data-testid={`abandoned-${r.id}`}
                                        className="border-t border-forest-50 hover:bg-forest-50/40"
                                    >
                                        <td className="px-4 py-3 font-semibold text-forest-800 flex items-center gap-2">
                                            <ShoppingCart className="w-3.5 h-3.5 text-gold-600" />
                                            {r.customer_name || "Anonymous"}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-forest-700/80">{r.phone || "—"}</td>
                                        <td className="px-4 py-3 text-xs text-forest-700/80">{r.city || "—"}</td>
                                        <td className="px-4 py-3 text-xs text-forest-700/80">{r.bundle_label || "—"}</td>
                                        <td className="px-4 py-3 text-xs">
                                            <div className="text-forest-700">
                                                {created.toLocaleString()}
                                            </div>
                                            <div
                                                className={`font-bold mt-0.5 ${ready ? "text-forest-700" : "text-amber-700"}`}
                                            >
                                                {ready ? "Ready to nudge" : "Wait ~1 hour"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <a
                                                    href={link || "#"}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`abandoned-nudge-${r.id}`}
                                                    onClick={(e) => {
                                                        if (!link) e.preventDefault();
                                                    }}
                                                    className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                                                        link
                                                            ? "bg-forest-800 text-gold-400 border-gold-500/40 hover:bg-forest-900"
                                                            : "bg-forest-50 text-forest-700/40 border-forest-100 cursor-not-allowed"
                                                    }`}
                                                >
                                                    Nudge on WhatsApp
                                                </a>
                                                <button
                                                    onClick={() => remove(r.id)}
                                                    className="p-2 rounded-lg hover:bg-rose-50"
                                                >
                                                    <Trash2 className="w-4 h-4 text-rose-600" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
